import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const MichiganCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Michigan - Instructional Shifts That Stick | Curipod"
      subTitle="See how Curipod brought engagement, confidence, and joy to Meads Mill Middle School in Northville Public Schools, Michigan."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroMain>
            <HeroContent>
              <HeroTag>CASE STUDY</HeroTag>
              <HeroTitle>Instructional Shifts That Stick</HeroTitle>
              <HeroSubtitle>A Northville Impact Story</HeroSubtitle>
              <HeroDescription>
                How a middle school in Northville Public Schools, Michigan ignited
                engagement with Curipod &mdash; sparking student voice, creative
                expression, and a renewed love for teaching.
              </HeroDescription>
              <HeroMeta>
                <MetaBadge>Northville Public Schools, MI</MetaBadge>
                <MetaBadge>Middle School ELA &amp; Social Studies</MetaBadge>
                <MetaBadge>Meads Mill Middle School</MetaBadge>
              </HeroMeta>
              <HeroCtaWrapper>
                <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                  <a>
                    <HeroCtaButton type="primary">Sign up for free</HeroCtaButton>
                  </a>
                </Link>
              </HeroCtaWrapper>
            </HeroContent>
            <HeroPhotoFrame>
              <HeroPhotoInner>
                <Image
                  src="/case-study-michigan/students-engaged.png"
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center"
                  alt="Students engaged with devices during a Curipod lesson at Meads Mill Middle School"
                  priority
                />
              </HeroPhotoInner>
            </HeroPhotoFrame>
          </HeroMain>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <FocusBadge>Student Engagement</FocusBadge>
                <FocusBadge>Instructional Support</FocusBadge>
                <FocusBadge>Student Confidence</FocusBadge>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* DOWNLOAD CTA (below hero) */}
      <DownloadCtaBanner>
        <MaxWidthContainer>
          <DownloadCtaInner>
            <DownloadCtaLabel>
              Want the full story? Download the PDF version of this case study.
            </DownloadCtaLabel>
            <a
              href="/Curipod_Case_Study_Michigan.pdf"
              download="Curipod_Case_Study_Michigan.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 44 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadCtaInner>
        </MaxWidthContainer>
      </DownloadCtaBanner>

      {/* THE SPARK */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>THE SPARK</SectionTag>
              <SectionTitle>Supporting Good Teaching</SectionTitle>
              <SectionText>
                When <strong>Jen Lawson</strong> first introduced Curipod to her district,
                it wasn&apos;t just about trying a new edtech tool. For the 25-year
                educator and Northville&apos;s Instructional Technology Integration
                Coordinator, it was about supporting good teaching&mdash;making it easier
                to plan, deliver, and sustain lessons that spark connection and curiosity.
              </SectionText>
              <SectionText>
                Northville has used Curipod to bring more energy into classrooms. For
                teachers like <strong>Amy Soukup</strong>, an English and Social Studies
                teacher at Meads Mill Middle School, it quickly became a go-to tool for
                driving engagement, building student confidence, and igniting her own
                excitement for teaching.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  They come in excited. They see Curipod on the screen and ask, &lsquo;Are
                  we doing Curipod?&rsquo; If I say no, I get a collective, &lsquo;Aw
                  man!&rsquo;
                </QuoteText>
                <QuoteAttrib>
                  &mdash; Amy Soukup, ELA &amp; Social Studies Teacher
                </QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamPhotoArea>
                <RoundedImageWrap>
                  <Image
                    src="/case-study-michigan/classroom-teaching.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Amy Soukup teaching with Curipod at Meads Mill Middle School"
                  />
                </RoundedImageWrap>
                <TeamPhotoCaption>
                  A Curipod lesson in action at Meads Mill
                </TeamPhotoCaption>
              </TeamPhotoArea>
              <TeamCardTitle>The Northville Team</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>Instructional Technology Integration Coordinator</TeamRole>
                  <TeamName>Jen Lawson</TeamName>
                  <TeamNote>
                    25-year educator championing instructional innovation
                  </TeamNote>
                </TeamMember>
                <TeamMember>
                  <TeamRole>ELA &amp; Social Studies Teacher</TeamRole>
                  <TeamName>Amy Soukup</TeamName>
                  <TeamNote>Meads Mill Middle School</TeamNote>
                </TeamMember>
              </TeamMemberList>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* ENGAGEMENT THAT STICKS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              ENGAGEMENT THAT STICKS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              The Shift Was Immediate
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Amy didn&apos;t begin using Curipod until May. But within weeks, her
              classroom looked&mdash;and sounded&mdash;very different. The shift was
              especially noticeable in students who rarely participated&mdash;those who
              hadn&apos;t responded to small group work, individual check-ins, or creative
              incentives.
            </SectionText>

            <ImpactGrid>
              <ImpactCard>
                <ImpactIcon>🗣️</ImpactIcon>
                <ImpactTitle>Student Voice Unlocked</ImpactTitle>
                <ImpactQuote>
                  &ldquo;That was my answer,&rdquo; a student beamed after seeing her
                  response projected&mdash;something she never would have shared aloud
                  before.
                </ImpactQuote>
              </ImpactCard>

              <ImpactCard>
                <ImpactIcon>🏆</ImpactIcon>
                <ImpactTitle>Quiet Students Thriving</ImpactTitle>
                <ImpactQuote>
                  One student, previously disengaged and quiet, now strives for the number
                  one spot each day&mdash;raising hands, typing thoughtful responses, and
                  competing for top-voted answers.
                </ImpactQuote>
              </ImpactCard>

              <ImpactCard>
                <ImpactIcon>🎨</ImpactIcon>
                <ImpactTitle>Creative Expression</ImpactTitle>
                <ImpactQuote>
                  Students laughed, debated, drew, reflected, and moved&mdash;engaging in
                  various learning modalities. The energy in the room changed who felt
                  empowered to speak up and how they did so.
                </ImpactQuote>
              </ImpactCard>

              <ImpactCard>
                <ImpactIcon>🔊</ImpactIcon>
                <ImpactTitle>Sustained Connection</ImpactTitle>
                <ImpactQuote>
                  For Amy, the energy didn&apos;t feel like a one-off. It felt like
                  sustained connection&mdash;a catalyst for student voice, creative
                  expression, and social interaction.
                </ImpactQuote>
              </ImpactCard>
            </ImpactGrid>

            <BlockQuote style={{ maxWidth: 680, margin: '48px auto 0' }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                A quiet classroom doesn&apos;t necessarily mean it&apos;s a learning
                classroom. With Curipod, I&apos;ve learned that louder sometimes means
                more learning.
              </QuoteText>
              <QuoteAttrib>
                &mdash; Amy Soukup, ELA &amp; Social Studies Teacher
              </QuoteAttrib>
            </BlockQuote>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* LESSON PLANNING FOR REAL CLASSROOMS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid $reverse>
            <ContentImageWrap>
              <Image
                src="/case-study-michigan/students-attentive.png"
                layout="fill"
                objectFit="cover"
                objectPosition="center"
                alt="Students working with laptops at Meads Mill Middle School"
              />
            </ContentImageWrap>

            <div>
              <SectionTag>LESSON PLANNING FOR REAL CLASSROOMS</SectionTag>
              <SectionTitle>Less Exhaustion, More Creativity</SectionTitle>
              <SectionText>
                Curipod wasn&apos;t developed in isolation. It&apos;s shaped through
                ongoing collaboration with educators&mdash;something Amy and Jen noticed
                right away.
              </SectionText>
              <SectionText>
                For Amy, planning lessons no longer meant spending hours trying to come up
                with new ways to keep students engaged. From vocabulary practice to test
                prep, Curipod helped her streamline the process&mdash;without sacrificing
                rigor or creativity.
              </SectionText>
              <SectionText>
                By removing some of the mental load, Curipod gave Amy more space to focus
                on what matters most: being present, energized, and connected with her
                students.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  I just type in the standards and Curipod puts it all together.
                  There&apos;s still my thought and ideas behind it but without the
                  exhausting process.
                </QuoteText>
                <QuoteAttrib>
                  &mdash; Amy Soukup, ELA &amp; Social Studies Teacher
                </QuoteAttrib>
              </BlockQuote>
            </div>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS — A NEW STANDARD */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              A NEW STANDARD FOR ENGAGEMENT
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              From One Classroom to the Whole District
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              What began as a short-term experiment in one classroom has grown into
              something larger for Northville. With tools that support real-time feedback,
              whole-class engagement, and creative freedom, teachers like Amy are
              discovering a new way to approach instruction.
            </SectionText>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                It just puts it all together and adds some creativity to it. That takes
                away your exhaustion. Teaching is already mentally exhausting. This gives
                you more of your life back.
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                &mdash; Amy Soukup, ELA &amp; Social Studies Teacher
              </QuoteAttrib>
            </ResultBlockQuote>

            <ResultsStudentsPhoto>
              <CircularImageWrap $size={160} $borderColor="rgba(249,151,70,0.6)">
                <Image
                  src="/case-study-michigan/students-engaged.png"
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center top"
                  alt="Northville students engaged in a Curipod lesson"
                />
              </CircularImageWrap>
              <ResultsPhotoLabel>Students at Meads Mill Middle School</ResultsPhotoLabel>
            </ResultsStudentsPhoto>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>Why It Matters</SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              In Amy&apos;s classroom, the change is visible&mdash;and audible. Laughter.
              Reflection. Debates. Applause. Students leaning in, not tuning out. This is
              the kind of change that happens when teachers are supported, students are
              heard, and classrooms come alive.
            </SectionText>

            <PeoplePhotoRow>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-michigan/classroom-teaching.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Teaching with Curipod"
                  />
                </CircularImageWrap>
                <PhotoLabel>Curipod in action</PhotoLabel>
              </PhotoItem>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-michigan/students-attentive.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Students working together"
                  />
                </CircularImageWrap>
                <PhotoLabel>Focused learning</PhotoLabel>
              </PhotoItem>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-michigan/students-engaged.png"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center top"
                    alt="Students engaged and smiling"
                  />
                </CircularImageWrap>
                <PhotoLabel>Students leaning in</PhotoLabel>
              </PhotoItem>
            </PeoplePhotoRow>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                Curipod doesn&apos;t just help you teach better. It makes you feel better.
              </FinalQuoteText>
              <QuoteAttrib>
                &mdash; Amy Soukup, ELA &amp; Social Studies Teacher
              </QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {['Engagement', 'Confidence', 'Student Voice', 'Lesson Planning'].map(
                tag => (
                  <Tag key={tag}>{tag}</Tag>
                ),
              )}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIconWrap aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIconWrap>
            <DownloadText>
              <DownloadTitle>Download the Original Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the Michigan case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_Michigan.pdf"
              download="Curipod_Case_Study_Michigan.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default MichiganCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  color: ${theme.colors.white};
  max-width: 640px;
`;

const HeroMain = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: 52px;
  padding: 90px 20px 58px;
  position: relative;
  z-index: 1;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 28px;
    padding: 74px 20px 40px;
  }
`;

const HeroPhotoFrame = styled.div`
  width: 100%;
  max-width: 560px;
  justify-self: end;
  padding: 14px;
  border-radius: 26px;
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.26) 0%,
    rgba(255, 255, 255, 0.08) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.36);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.26);

  @media ${MOBILE} {
    max-width: 100%;
    justify-self: stretch;
    padding: 10px;
  }
`;

const HeroPhotoInner = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);

  @media ${MOBILE} {
    aspect-ratio: 16 / 10;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroCtaWrapper = styled.div`
  margin-top: 36px;
`;

const HeroCtaButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 48px;
  font-size: 20px;
  height: 58px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.35);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${MOBILE} {
    font-size: 18px;
    height: 52px;
    padding: 0 36px;
  }
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 32px 0;
`;

const StatBannerInner = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 20px;
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const FocusBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: bold;
  padding: 10px 24px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

/* ─── DOWNLOAD CTA BANNER (below hero) ──────────────────────────────────────── */

const DownloadCtaBanner = styled.div`
  background: ${theme.colors.beigeDark};
  border-bottom: 1px solid rgba(249, 151, 70, 0.2);
  padding: 16px 0;
`;

const DownloadCtaInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
`;

const DownloadCtaLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${theme.colors.text.dark};

  @media ${MOBILE} {
    font-size: 14px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CIRCULAR IMAGE ─────────────────────────────────────────────────────────── */

const CircularImageWrap = styled.div<{ $size: number; $borderColor?: string }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid ${({ $borderColor }) => $borderColor ?? theme.colors.orange};
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div<{ $reverse?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  ${({ $reverse }) =>
    $reverse &&
    css`
      & > *:first-child {
        order: 1;
      }
      & > *:last-child {
        order: 2;
      }
      @media ${MOBILE} {
        & > *:first-child {
          order: 2;
        }
        & > *:last-child {
          order: 1;
        }
      }
    `}
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamPhotoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 2px solid ${theme.colors.beigeDark};
`;

const RoundedImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

const TeamPhotoCaption = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  margin-top: 10px;
  font-style: italic;
  text-align: center;
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const TeamNote = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  margin-top: 2px;
`;

/* ─── CONTENT IMAGE ──────────────────────────────────────────────────────────── */

const ContentImageWrap = styled.div`
  position: relative;
  width: 100%;
  min-height: 340px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    min-height: 240px;
  }
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

const ResultsStudentsPhoto = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ResultsPhotoLabel = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  text-align: center;
  margin: 0;
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const PeoplePhotoRow = styled.div`
  display: flex;
  gap: 32px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 48px;

  @media ${MOBILE} {
    gap: 20px;
    margin-bottom: 32px;
  }
`;

const PhotoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const PhotoLabel = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  text-align: center;
  margin: 0;
`;

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIconWrap = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
